import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Mail, MailX, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { Database } from '../lib/database.types';

type EmailPreferences = Database['public']['Tables']['email_preferences']['Row'];
type EmailPreferencesInsert = Database['public']['Tables']['email_preferences']['Insert'];

// Valid email preference keys that can be updated
const VALID_EMAIL_PREFERENCE_KEYS = [
  'marketing_emails',
  'activity_digest', 
  'project_notifications',
  'usage_alerts'
] as const;

type ValidEmailPreferenceKey = typeof VALID_EMAIL_PREFERENCE_KEYS[number];

// Type guard to check if a string is a valid email preference key
function isValidEmailPreferenceKey(key: string): key is ValidEmailPreferenceKey {
  return VALID_EMAIL_PREFERENCE_KEYS.includes(key as ValidEmailPreferenceKey);
}

// Helper interface for upsert operations with required user_id
interface EmailPreferencesUpsert extends Omit<EmailPreferencesInsert, 'user_id'> {
  user_id: string;
}

export function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<EmailPreferences | null>(null);
  
  const token = searchParams.get('token');
  const email = searchParams.get('email');
  const type = searchParams.get('type'); // specific email type to unsubscribe from

  useEffect(() => {
    if (token && email) {
      handleUnsubscribe();
    } else {
      setError('Invalid unsubscribe link');
      setLoading(false);
    }
  }, [token, email]);

  const handleUnsubscribe = useCallback(async () => {
    try {
      setLoading(true);
      
      // Validate token by checking if it matches the email hash
      // In a real implementation, you'd verify this token against a stored value
      const expectedToken = btoa(email!).replace(/=/g, ''); // Simple encoding for demo
      
      if (token !== expectedToken) {
        setError('Invalid or expired unsubscribe link');
        return;
      }

      // Get user by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id')
        .eq('email', email!)
        .single();

      if (userError || !userData) {
        setError('User not found');
        return;
      }

      // Update email preferences
      if (type) {
        // Validate the type parameter
        if (!isValidEmailPreferenceKey(type)) {
          setError('Invalid email preference type');
          return;
        }

        // Unsubscribe from specific type
        const updateData: EmailPreferencesUpsert = {
          user_id: userData.id,
          updated_at: new Date().toISOString()
        };
        updateData[type] = false;

        const { error: updateError } = await supabase
          .from('email_preferences')
          .upsert(updateData);

        if (updateError) {
          setError('Failed to update preferences');
          return;
        }

        // If unsubscribing from marketing emails, remove from Resend
        if (type === 'marketing_emails') {
          try {
            const { EmailService } = await import('../lib/email-service');
            await EmailService.removeFromResend(email!, userData.id);
            console.log('✅ User removed from Resend marketing audience');
          } catch (resendError) {
            console.error('Error removing from Resend:', resendError);
            // Don't fail the unsubscribe - preference was updated
          }
        }
      } else {
        // Unsubscribe from all marketing emails
        const { error: updateError } = await supabase
          .from('email_preferences')
          .upsert({
            user_id: userData.id,
            marketing_emails: false,
            activity_digest: false,
            updated_at: new Date().toISOString()
          });

        if (updateError) {
          setError('Failed to update preferences');
          return;
        }

        // Remove from Resend marketing audience
        try {
          const { EmailService } = await import('../lib/email-service');
          await EmailService.removeFromResend(email!, userData.id);
          console.log('✅ User removed from Resend marketing audience');
        } catch (resendError) {
          console.error('Error removing from Resend:', resendError);
          // Don't fail the unsubscribe - preference was updated
        }
      }

      // Get updated preferences
      const { data: prefs } = await supabase
        .from('email_preferences')
        .select('*')
        .eq('user_id', userData.id)
        .single();

      setPreferences(prefs);
      setUserEmail(email);
      setSuccess(true);
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token, email, type]);

  const handleResubscribe = async (prefType: string) => {
    try {
      // Validate the preference type
      if (!isValidEmailPreferenceKey(prefType)) {
        console.error('Invalid preference type:', prefType);
        return;
      }
      
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('email', userEmail!)
        .single();

      if (userData) {
        const updateData: EmailPreferencesUpsert = {
          user_id: userData.id,
          updated_at: new Date().toISOString()
        };
        updateData[prefType] = true;
        
        await supabase
          .from('email_preferences')
          .upsert(updateData);

        // Refresh preferences
        const { data: prefs } = await supabase
          .from('email_preferences')
          .select('*')
          .eq('user_id', userData.id)
          .single();

        setPreferences(prefs);
      }
    } catch (err) {
      console.error('Resubscribe error:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-blue-50 flex items-center justify-center p-4">
      <Helmet>
        <title>Unsubscribe - Rulevia</title>
      </Helmet>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {loading ? (
            <div className="text-center">
              <RefreshCw className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Processing your request...</p>
            </div>
          ) : error ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribe Failed</h1>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={() => navigate('/login')}
                className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Go to Login
              </button>
            </div>
          ) : success ? (
            <div>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MailX className="w-8 h-8 text-green-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Unsubscribed Successfully</h1>
                <p className="text-gray-600">
                  You've been unsubscribed from {type ? `${type.replace(/_/g, ' ')}` : 'marketing emails'}.
                </p>
              </div>

              {preferences && (
                <div className="space-y-4 mb-8">
                  <h3 className="font-medium text-gray-900">Your current email preferences:</h3>
                  <div className="space-y-2">
                    {([
                      { key: 'project_notifications', label: 'Project Notifications' },
                      { key: 'usage_alerts', label: 'Usage Alerts' },
                      { key: 'marketing_emails', label: 'Marketing Emails' },
                      { key: 'activity_digest', label: 'Weekly Digest' }
                    ] as const).map(pref => (
                      <div key={pref.key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-700">{pref.label}</span>
                        {preferences[pref.key as ValidEmailPreferenceKey] ? (
                          <span className="text-green-600 text-sm flex items-center">
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Subscribed
                          </span>
                        ) : (
                          <button
                            onClick={() => handleResubscribe(pref.key)}
                            className="text-purple-600 text-sm hover:text-purple-700"
                          >
                            Resubscribe
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="text-center space-y-3">
                <button
                  onClick={() => navigate('/settings')}
                  className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors w-full"
                >
                  Manage All Preferences
                </button>
                <button
                  onClick={() => navigate('/')}
                  className="text-purple-600 hover:text-purple-700 text-sm"
                >
                  Return to Dashboard
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <p className="text-center text-gray-500 text-sm mt-6">
          <Mail className="w-4 h-4 inline mr-1" />
          You can always manage your email preferences in your account settings.
        </p>
      </motion.div>
    </div>
  );
}